import { useEffect, useMemo } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "../app/store";
import { OrgThemeProvider } from "../providers/orgThemeProvider";
import { KindeWrapper } from "../providers/kindeProvider";
import { AccessControlProvider } from "../context/AccessControlContext";
import { setTokenGetter } from "../features/axiosInstance";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { fetchPublicRankings } from "../features/organisation/organisationSlice";
import OrgRankingPage from "./OrgRankingPage";
import { slugFromPath } from "./publicLocation";

/** Wires the Kinde token into the shared axios instance inside the public
 *  tree (only mounted when previewing on the primary domain). */
function AuthBridge({ children }) {
  const { getAccessToken } = useKindeAuth();

  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  return children;
}

function PublicShell() {
  const dispatch = useDispatch();
  const organisation = useSelector((state) => state.organisation.organisation);

  // Only set when previewing on the main domain; on an organisation's own
  // domain the server resolves it from the request host.
  const slug = useMemo(() => slugFromPath(), []);

  // One request returns both the branding and the table.
  useEffect(() => {
    dispatch(fetchPublicRankings({ slug }));
  }, [dispatch, slug]);

  return (
    <OrgThemeProvider organisation={organisation}>
      <OrgRankingPage />
    </OrgThemeProvider>
  );
}

/**
 * Root of the public organisation tree.
 *
 * On an organisation's own domain the tree deliberately contains no
 * KindeWrapper, no Layout and no ProtectedRoute: the authenticated tree would
 * block on Kinde initialising and then redirect to a callback URI that the
 * custom domain does not have registered.
 *
 * When previewing on the PRIMARY domain (a /rankings/:slug path), the Kinde
 * session already lives on this origin, so we DO mount the auth context -
 * that is what lets a logged-in admin see the calculation breakdown on the
 * organisation page while regular visitors see nothing different.
 */
export default function PublicApp() {
  const previewOnPrimary = Boolean(slugFromPath(window.location.pathname));

  if (!previewOnPrimary) {
    return (
      <Provider store={store}>
        <PublicShell />
      </Provider>
    );
  }

  return (
    <Provider store={store}>
      <KindeWrapper>
        <AuthBridge>
          <AccessControlProvider>
            <PublicShell />
          </AccessControlProvider>
        </AuthBridge>
      </KindeWrapper>
    </Provider>
  );
}
